import React from "react";
import { Link } from "react-router-dom";

import { formatBytes, formatNumber } from "utils/format";
import ContactSupportLink from "ui/ContactSupportLink";
import Loading from "ui/Loading";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import PanelTable from "ui/PanelTable";
import Popover from "ui/Popover";

import { useIndexCheckQuery, IndexCheckQuery } from "./gql/Query.generated";
import styles from "./style.module.scss";
import { useRoutes } from "utils/routes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faInfoCircle } from "@fortawesome/pro-solid-svg-icons";

type IndexCheckTable = IndexCheckQuery["getQueryIndexCheck"]["tables"][number];
type SchemaTable = IndexCheckTable["table"];
type SchemaColumn = IndexCheckTable["columnsIndexed"][number]["column"];
type SchemaIndex = IndexCheckTable["columnsIndexed"][number]["index"];

type Props = {
  databaseId: string;
  queryId: string;
};

const IndexCheck: React.FunctionComponent<Props> = ({
  databaseId,
  queryId,
}) => {
  const { data, loading, error } = useIndexCheckQuery({
    variables: { queryId },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }
  const { tables, timedOut, failedToParse } = data.getQueryIndexCheck;

  return (
    <div>
      {tables.map((t) => (
        <TableIndexCheck key={t.table.id} databaseId={databaseId} check={t} />
      ))}
      {tables.length === 0 && !timedOut && !failedToParse && (
        <Panel title="Index Check Failed">
          <PanelSection>
            <p>
              <strong>Error:</strong> Index Check could not recognize any of the
              tables in the query.
            </p>
            <p>
              This sometimes happens when your tables don't have recent
              statistics. If this issue persists please contact support.
            </p>
          </PanelSection>
        </Panel>
      )}
      {timedOut && (
        <Panel title="Index Check Timed Out">
          <PanelSection>
            <strong>Warning:</strong> Due to complexity, only parts of this
            query could be analyzed.
          </PanelSection>
        </Panel>
      )}
      {failedToParse && (
        <Panel title="Index Check Failed">
          <PanelSection>
            <p>
              <strong>Error:</strong> Could not parse query.
            </p>
            <p>
              The query string might have been truncated. Please{" "}
              <ContactSupportLink>contact support</ContactSupportLink> to learn
              more.
            </p>
          </PanelSection>
        </Panel>
      )}
    </div>
  );
};

const TableIndexCheck: React.FunctionComponent<{
  databaseId: string;
  check: IndexCheckTable;
}> = ({ databaseId, check }) => {
  const { databaseTable } = useRoutes();
  return (
    <Panel
      title={
        <Link to={databaseTable(databaseId, check.table.id)}>
          {check.table.tableName}
        </Link>
      }
      key={check.table.id}
    >
      <PanelTable horizontal>
        <tbody>
          <tr>
            <th>Table Size</th>
            <td>{formatBytes(check.table.lastStats?.dataSizeBytes)}</td>
            <th>Total Visible Rows</th>
            <td>{formatNumber(check.table.lastStats?.liveTuples)}</td>
          </tr>
        </tbody>
      </PanelTable>
      {(check.columnsIndexed.length > 0 ||
        check.columnsMissingIndex.length > 0) && (
        <PanelSection>
          <h5 className={styles.checkTitle}>Index Check</h5>
          <div className={styles.indexCheckOk}>
            {check.columnsIndexed.map((ci) => (
              <IndexCheckOk
                key={ci.column.name}
                index={ci.index}
                column={ci.column}
                databaseId={databaseId}
              />
            ))}
          </div>
          <div className={styles.indexCheckMissing}>
            {check.columnsMissingIndex.map((column) => (
              <IndexCheckMissing
                key={column.name}
                table={check.table}
                column={column}
                databaseId={databaseId}
              />
            ))}
          </div>
        </PanelSection>
      )}
    </Panel>
  );
};

const IndexCheckOk: React.FunctionComponent<{
  databaseId: string;
  column: SchemaColumn;
  index: SchemaIndex;
}> = ({ databaseId, index, column }) => {
  const { databaseIndex } = useRoutes();
  return (
    <Popover
      popupClassName={styles.popoverPopup}
      title={<span className={styles.popoverTitle}>{index.name}</span>}
      content={
        <span className={styles.popoverContent}>{index.definitionShort}</span>
      }
      key={column.name}
      placement="top"
    >
      <Link to={databaseIndex(databaseId, index.id)}>
        <FontAwesomeIcon icon={faCheckCircle} />
        <span className={styles.column}>{column.name}</span>
      </Link>
    </Popover>
  );
};

const IndexCheckMissing: React.FunctionComponent<{
  databaseId: string;
  table: SchemaTable;
  column: SchemaColumn;
}> = ({ databaseId, table, column }) => {
  const { databaseTable } = useRoutes();
  return (
    <Popover
      content="Column might be missing an Index"
      key={column.name}
      placement="top"
    >
      <Link to={databaseTable(databaseId, table.id)}>
        <FontAwesomeIcon icon={faInfoCircle} />
        <span className={styles.column}>{column.name}</span>
      </Link>
    </Popover>
  );
};

export default IndexCheck;
